import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CarrierIssueType } from "@freightsimple/generated-carriers-openapi-client/models/CarrierIssueType";
import { type OperationalIssue } from "@freightsimple/generated-carriers-openapi-client/models/OperationalIssue";
import { describeIssueType } from "@/App/Views/carrierFormatting";
import { questionFor } from "@/App/Shipment/issueQuestions";
import { IssueResponseForm } from "@/App/Shipment/IssueResponseForm";

// How loud the banner is. Freight that is late or lost is a service failure and
// gets the red treatment; the rest is work that needs doing, not a failure.
const ISSUE_VARIANTS: Record<
  CarrierIssueType,
  "danger" | "warning" | "info" | "neutral"
> = {
  [CarrierIssueType.MissingFreight]: "danger",
  [CarrierIssueType.MissedPickup]: "danger",
  [CarrierIssueType.MissedDelivery]: "danger",
  [CarrierIssueType.ExpectedDeliveryIsInThePast]: "warning",
  [CarrierIssueType.TrackingNotUpdated]: "warning",
  [CarrierIssueType.BookPickup]: "info",
  [CarrierIssueType.PendingCustomsClearance]: "neutral",
};

/**
 * One open issue, at the top of the shipment it belongs to.
 *
 * The banner carries its own response action, so a carrier who follows a link
 * from an email lands on the shipment and can answer without going anywhere
 * else. Customs holds are the exception — we watch those, the carrier has
 * nothing to submit — so they render as a notice with no button.
 */
export function IssueBanner({
  issue,
  onResponded,
}: {
  issue: OperationalIssue;
  onResponded: () => void;
}) {
  const question = questionFor(issue.issueType);

  return (
    <Banner
      variant={ISSUE_VARIANTS[issue.issueType]}
      title={describeIssueType(issue.issueType)}
      actions={
        question ? (
          <Button
            primary
            dialog={() => (
              <IssueResponseForm
                issue={issue}
                question={question}
                onResponded={onResponded}
              />
            )}
          >
            {question.action}
          </Button>
        ) : undefined
      }
    >
      <Stack gap={1}>
        <T size="sm">
          {question?.prompt ??
            "This shipment is held pending customs clearance. Nothing is needed from you — we'll let you know when it releases."}
        </T>
        {/* Whatever Operations wrote on the task, when they wrote one. It says
            what's specific about this shipment that the prompt can't. */}
        {issue.title && (
          <T size="sm" color="secondary">
            {issue.title}
          </T>
        )}
      </Stack>
    </Banner>
  );
}
